import { Checkbox, Tooltip } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

import { DogApi, DogGridDto, DogSearch } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { AddButton, EditButton, FusionButton, SearchTablePage, ViewButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface IDogListPageProps {}

const IconContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
`;
export const DogListPage: React.FunctionComponent<IDogListPageProps> = (props) => {
  const { t, tUnsafe } = useTl();
  const api = useApiService(DogApi);
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const selectionList = React.useMemo(() => [], []);

  const fuse = React.useCallback(() => {
    if (selectionList.length !== 2) {
      showError(t(ETLCodes.FuseSelectionDogError));
    } else {
      window.localStorage.setItem("dogFusionId", JSON.stringify(selectionList[1].dogId));
      history.push(`${ERoutes.dogFusion}/${selectionList[0].dogId}/fusion/edit`);
    }
  }, [history, selectionList, t]);

  const columns = React.useMemo<IDataTableColumn<DogGridDto>[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DogGridDto) => (
          <ButtonContainer>
            <ViewButton minimal={true} onClick={() => history.push(`${ERoutes.dog}/${row.dogId}/detail/view`)} />
            <EditButton minimal={true} onClick={() => history.push(`${ERoutes.dog}/${row.dogId}/detail/edit`)} />
          </ButtonContainer>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "check",
        autoFitContent: true,
        render: (row: DogGridDto) => (
          <Checkbox
            checked={selectionList.find((c) => c === row)}
            onChange={() => {
              if (selectionList.includes(row)) {
                const index = selectionList.findIndex((e) => e === row);
                selectionList.splice(index, 1);
              } else {
                selectionList.push(row);
              }
            }}
          />
        ),
      },
      {
        header: () => "",
        fieldName: "icons",
        alignment: "center",
        computed: true,
        disableSorting: true,
        render: (row: DogGridDto) => (
          <IconContainer>
            {row.isDead && <i className="fas fa-cross" />}
            {row.isExcluded && <i className="fas fa-ban" />}
          </IconContainer>
        ),
      },
      {
        header: () => t(ETLCodes.CompleteName),
        fieldName: "computedName",
      },
      {
        header: () => t(ETLCodes.Breed),
        fieldName: "breedName",
        computed: true,
        disableSorting: true,
        render: (row: DogGridDto) => (
          <Link target="_blank" to={`${ERoutes.breeds}/${row.breedId}/detail/view`}>
            {row.breedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Color),
        fieldName: "color",
        computed: true,
        disableSorting: true,
        render: (row: DogGridDto) => (
          <Link target="_blank" to={`${ERoutes.color}/${row.colorId}/detail/view`}>
            {row.color}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Sex),
        fieldName: "sex",
        computed: true,
        disableSorting: true,
        render: (row: DogGridDto) => <div>{tUnsafe(row.sex)}</div>,
      },
      {
        header: () => t(ETLCodes.Pedigree),
        fieldName: "pedigrees",
        computed: true,
        disableSorting: true,
        render: (value: DogGridDto) => {
          if (value.pedigrees?.length > 0) {
            return (
              <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value.pedigree}
                {value.pedigrees.length > 1 && (
                  <span>
                    &nbsp;
                    <Tooltip
                      content={
                        <div>
                          {value.pedigrees.map((pedigree, i) => (
                            <div key={i}>{pedigree}</div>
                          ))}
                        </div>
                      }
                      position="right"
                    >
                      <div style={{ cursor: "pointer" }}>...</div>
                    </Tooltip>
                  </span>
                )}
              </span>
            );
          } else {
            return <div />;
          }
        },
      },
      {
        header: () => t(ETLCodes.Identification1),
        computed: true,
        disableSorting: true,
        fieldName: "dogChip",
      },
      {
        header: () => t(ETLCodes.Identification2),
        computed: true,
        disableSorting: true,
        fieldName: "secondDogChip",
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "type",
        computed: true,
        disableSorting: true,
      },
    ],
    [history, selectionList, t, tUnsafe]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.dog}/0/detail/edit`);
  }, [history]);

  const customizeRowStyleCallback = React.useCallback(() => {
    return css`
      vertical-align: middle !important;
    `;
  }, []);

  const getCriterias = React.useCallback(() => api.getSearchCriterias(true), [api]);

  const search = React.useCallback(
    (sObj: any) => {
      //api.abortPendingRequest();
      return api.baseSearch(DogSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId }));
    },
    [api, currentLanguage.languageId]
  );

  const exportFunction = React.useCallback(
    (sObj) => api.exportXlsFile(DogSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId })),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      filterPlaceholder={t(ETLCodes.DogListFilterPlaceholder)}
      getCriteriasFunction={getCriterias}
      searchFunction={search}
      exportFunction={exportFunction}
      sortKeys={{ computedName: "ASC" }}
      breadCrumbs={[{ text: t(ETLCodes.Dogs) }]}
      overrideListValues={{ Sex: (v) => tUnsafe(v.displayValue) }}
      customizeRowStyle={customizeRowStyleCallback}
      rightElement={
        <>
          <AddButton onClick={addItemFunction} text={t(ETLCodes.General_Add)} />
          <FusionButton onClick={fuse} text={t(ETLCodes.General_Group)} />
        </>
      }
      filterMode="OnEnter"
    />
  );
};
